import { LogLevel } from '../types/LogLevel';
import { LOGGER_CONFIG } from '../constants/LOGGER_CONFIG';
import { getEnvironment, getEnvironmentValue, ENVIRONMENTS, ENVIRONMENT_VALUES } from '@shared/utils';
import type { Result, AppError } from '@shared/utils';
import { ok, err } from '@shared/utils';

// Valid log levels for validation
const VALID_LOG_LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error'];

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Get the current log level for the environment using centralized environment utilities</blockquote>
 * 
 * @example
 * ```typescript
 * const result = getCurrentLogLevel();
 * result.match({
 *   ok: (level) => {
 *     console.log(`Current log level: ${level}`);
 *   },
 *   err: (error) => {
 *     console.error(`Failed to get log level: ${error.errorMessage}`);
 *   }
 * });
 * ```
 **/
export const getCurrentLogLevel = (): Result<LogLevel, AppError> => {
  const envResult = getEnvironment();
  
  return envResult.match({
    ok: (env) => {
      // Try to get LOG_LEVEL from environment variable
      const logLevelResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.LOG_LEVEL);
      
      return logLevelResult.match({
        ok: (envLogLevel) => {
          // Validate that the environment variable is a valid LogLevel
          if (VALID_LOG_LEVELS.includes(envLogLevel as LogLevel)) {
            return ok(envLogLevel as LogLevel);
          } else {
            return err({
              errorCode: 'INVALID_LOG_LEVEL',
              errorMessage: `Invalid LOG_LEVEL environment variable: ${envLogLevel}`
            });
          }
        },
        err: () => {
          // No LOG_LEVEL set, use default from LOGGER_CONFIG
          const defaultLevel = LOGGER_CONFIG.levels[env];
          if (defaultLevel) {
            return ok(defaultLevel);
          } else {
            return err({
              errorCode: 'NO_LOG_LEVEL_CONFIG',
              errorMessage: `No log level configured for environment: ${env}`
            });
          }
        }
      });
    },
    err: (error) => {
      // Environment detection failed, try to use development defaults
      const logLevelResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.LOG_LEVEL);
      
      return logLevelResult.match({
        ok: (envLogLevel) => {
          if (VALID_LOG_LEVELS.includes(envLogLevel as LogLevel)) {
            return ok(envLogLevel as LogLevel);
          } else {
            return err({
              errorCode: 'INVALID_LOG_LEVEL',
              errorMessage: `Invalid LOG_LEVEL environment variable: ${envLogLevel}`
            });
          }
        },
        err: () => {
          // Fallback to development log level
          const defaultLevel = LOGGER_CONFIG.levels[ENVIRONMENTS.DEVELOPMENT];
          if (defaultLevel) {
            return ok(defaultLevel);
          } else {
            return err({
              errorCode: 'NO_LOG_LEVEL_CONFIG',
              errorMessage: `No log level configured for development environment`
            });
          }
        }
      });
    }
  });
}; 